import { useState, useEffect } from 'react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import RichTextEditor from '../../components/feature/RichTextEditor';
import ImageUploader from '../../components/feature/ImageUploader';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL ?? '';
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY ?? '';
const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey);

/* Fixed: switched from `interface` to a `type` alias to avoid the
   parsing error that complained about a missing `}` before the first
   property declaration. The shape of the data remains unchanged. */
type BlogPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  image_url: string;
  published_at: string;
  status: 'draft' | 'published';
};

export default function AdminPanel() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published');
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image_url: '',
    status: 'draft' as 'draft' | 'published',
  });

  useEffect(() => {
    checkAuth();
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
  };

  const checkAuth = async () => {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) throw error;
      setIsAuthenticated(!!session);
    } catch (err) {
      console.error('Auth check failed:', err);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) setPosts(data as BlogPost[]);
    } catch (err) {
      console.error('Failed to load posts:', err);
      showNotification('error', 'Não foi possível carregar os artigos.');
    }
  };

  const handleLogin = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      setIsAuthenticated(true);
      await loadPosts();
    } catch (err) {
      console.error('Login error:', err);
      showNotification('error', 'Erro ao fazer login. Verifique suas credenciais.');
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (title: string) => {
    setFormData((prev) => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title),
    }));
  };

  const handleSubmit = async (e: React.FormEvent, saveAsDraft = false) => {
    e.preventDefault();
    setSaving(true);

    // Validar campos obrigatórios
    if (!formData.title.trim()) {
      showNotification('error', 'O título é obrigatório.');
      setSaving(false);
      return;
    }

    if (!formData.slug.trim()) {
      showNotification('error', 'O slug (URL) é obrigatório.');
      setSaving(false);
      return;
    }

    if (!formData.excerpt.trim()) {
      showNotification('error', 'O resumo é obrigatório. Por favor, preencha o campo de resumo.');
      setSaving(false);
      return;
    }

    if (!formData.content.trim()) {
      showNotification('error', 'O conteúdo é obrigatório.');
      setSaving(false);
      return;
    }

    // Preparar dados com todos os campos obrigatórios preenchidos
    const dataToSave = {
      title: formData.title.trim(),
      slug: formData.slug.trim(),
      excerpt: formData.excerpt.trim(),
      content: formData.content.trim(),
      image_url: formData.image_url.trim() || null,
      category: 'Geral',
      author: 'Admin',
      author_avatar: null,
      reading_time: '5 min',
      status: saveAsDraft ? 'draft' : 'published',
      published_at: saveAsDraft ? null : new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    try {
      if (editingPost) {
        // Atualizar artigo existente
        const { error } = await supabase
          .from('posts')
          .update(dataToSave)
          .eq('id', editingPost.id);
        
        if (error) {
          console.error('Erro ao atualizar:', error);
          throw error;
        }
        showNotification('success', saveAsDraft ? 'Rascunho guardado!' : 'Artigo atualizado!');
      } else {
        // Criar novo artigo
        const { error } = await supabase
          .from('posts')
          .insert([{
            ...dataToSave,
            created_at: new Date().toISOString(),
          }]);
        
        if (error) {
          console.error('Erro ao criar:', error);
          throw error;
        }
        showNotification('success', saveAsDraft ? 'Rascunho guardado!' : 'Artigo publicado!');
      }

      resetForm();
      await loadPosts();
    } catch (err: any) {
      console.error('Erro ao guardar:', err);
      
      // Mensagens de erro mais específicas
      if (err.message?.includes('duplicate')) {
        showNotification('error', 'Já existe um artigo com este slug (URL). Por favor, use outro.');
      } else if (err.message?.includes('null value')) {
        showNotification('error', 'Todos os campos obrigatórios devem ser preenchidos.');
      } else {
        showNotification('error', `Erro ao guardar: ${err.message || 'Tente novamente.'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePublishDraft = async (post: BlogPost) => {
    try {
      const { error } = await supabase
        .from('posts')
        .update({
          status: 'published',
          published_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', post.id);

      if (error) throw error;
      showNotification('success', 'Artigo publicado com sucesso!');
      await loadPosts();
    } catch (err) {
      console.error('Publish error:', err);
      showNotification('error', 'Não foi possível publicar o artigo.');
    }
  };

  const handleUnpublish = async (post: BlogPost) => {
    try {
      const { error } = await supabase
        .from('posts')
        .update({
          status: 'draft',
          updated_at: new Date().toISOString(),
        })
        .eq('id', post.id);

      if (error) throw error;
      showNotification('success', 'Artigo movido para rascunhos.');
      await loadPosts();
    } catch (err) {
      console.error('Unpublish error:', err);
      showNotification('error', 'Não foi possível mover o artigo para rascunhos.');
    }
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      image_url: post.image_url,
      status: post.status ?? 'published',
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este artigo?')) return;

    try {
      const { error } = await supabase.from('posts').delete().eq('id', id);
      if (error) throw error;
      showNotification('success', 'Artigo excluído!');
      await loadPosts();
    } catch (err) {
      console.error('Delete error:', err);
      showNotification('error', 'Não foi possível excluir o artigo.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image_url: '',
      status: 'draft',
    });
    setEditingPost(null);
    setShowForm(false);
  };

  const publishedPosts = posts.filter((p) => p.status === 'published' || !p.status);
  const draftPosts = posts.filter((p) => p.status === 'draft');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-red-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">A carregar...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {notification && (
        <div
          className={`fixed top-20 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in ${
            notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          <i className={notification.type === 'success' ? 'ri-check-line text-xl' : 'ri-error-warning-line text-xl'}></i>
          {notification.message}
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowPreview(false)}></div>
          <div className="relative min-h-screen flex items-start justify-center py-8 px-4">
            <div className="relative bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden">
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full">
                    <i className="ri-eye-line text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Pré-visualização do Artigo</h3>
                    <p className="text-xs text-gray-500">Veja como o artigo ficará quando publicado</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>

              <div className="p-8">
                {formData.image_url ? (
                  <div className="relative h-80 rounded-xl overflow-hidden mb-8">
                    <img src={formData.image_url} alt={formData.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  </div>
                ) : (
                  <div className="h-80 rounded-xl bg-gray-200 flex items-center justify-center mb-8">
                    <div className="text-center text-gray-400">
                      <i className="ri-image-line text-5xl"></i>
                      <p className="mt-2 text-sm">Sem imagem de capa</p>
                    </div>
                  </div>
                )}

                <h1
                  className="text-4xl font-bold text-gray-900 mb-4 leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {formData.title || 'Título do Artigo'}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mb-6 pb-6 border-b border-gray-200">
                  <p className="text-sm text-gray-500">
                    {new Date().toLocaleDateString('pt-PT', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>

                {formData.excerpt && (
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed italic border-l-4 border-red-500 pl-4">
                    {formData.excerpt}
                  </p>
                )}

                <div
                  className="prose prose-lg max-w-none prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-red-600 prose-blockquote:border-l-red-500 prose-blockquote:text-gray-600 prose-blockquote:italic prose-img:rounded-xl"
                  dangerouslySetInnerHTML={{
                    __html:
                      formData.content ||
                      '<p class="text-gray-400">O conteúdo do artigo aparecerá aqui...</p>',
                  }}
                />
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  <i className="ri-information-line mr-1"></i>
                  Esta é uma pré-visualização. O artigo ainda não foi publicado.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Voltar ao Editor
                  </button>
                  <button
                    onClick={(e) => {
                      setShowPreview(false);
                      handleSubmit(e as any, false);
                    }}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-send-plane-line"></i>
                    Publicar Agora
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-8">
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Painel de Administração
                </h1>
                <p className="text-gray-600">Gerir artigos do blog</p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    resetForm();
                    setShowForm(!showForm);
                  }}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium whitespace-nowrap flex items-center gap-2 cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  Novo Artigo
                </button>
                <button
                  onClick={handleLogout}
                  className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium whitespace-nowrap flex items-center gap-2 cursor-pointer"
                >
                  <i className="ri-logout-box-line"></i>
                  Sair
                </button>
              </div>
            </div>
          </div>

          {showForm && (
            <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {editingPost ? 'Editar Artigo' : 'Criar Novo Artigo'}
                </h2>
                {editingPost && (
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      editingPost.status === 'draft' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {editingPost.status === 'draft' ? 'Rascunho' : 'Publicado'}
                  </span>
                )}
              </div>

              <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      placeholder="O título do seu artigo"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Slug (URL)</label>
                    <div className="flex items-center">
                      <span className="text-gray-500 text-sm mr-2">/blog/</span>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                        placeholder="url-do-artigo"
                        required
                      />
                    </div>
                  </div>
                </div>

                <ImageUploader
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  label="Imagem de Capa"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resumo <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Uma breve descrição do artigo que aparecerá nas listagens (obrigatório)"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.excerpt.length}/500 caracteres</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Conteúdo</label>
                  <p className="text-xs text-gray-500 mb-2">
                    <i className="ri-information-line mr-1"></i>
                    Use o editor para formatar o texto e adicionar imagens ao conteúdo
                  </p>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(html) => setFormData({ ...formData, content: html })}
                    placeholder="Escreva o conteúdo completo do artigo aqui..."
                  />
                </div>

                <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowPreview(true)}
                    className="bg-gray-800 text-white px-8 py-3 rounded-lg hover:bg-gray-900 transition-colors font-medium whitespace-nowrap flex items-center gap-2 cursor-pointer"
                  >
                    <i className="ri-eye-line"></i>
                    Pré-visualizar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium whitespace-nowrap flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                    {editingPost ? 'Atualizar e Publicar' : 'Publicar Artigo'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e as any, true)}
                    disabled={saving}
                    className="bg-yellow-500 text-white px-8 py-3 rounded-lg hover:bg-yellow-600 transition-colors font-medium whitespace-nowrap flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-draft-line"></i>
                    Guardar como Rascunho
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('published')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors cursor-pointer ${
                  activeTab === 'published'
                    ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <i className="ri-article-line mr-2"></i>
                Publicados ({publishedPosts.length})
              </button>
              <button
                onClick={() => setActiveTab('drafts')}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors cursor-pointer ${
                  activeTab === 'drafts'
                    ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <i className="ri-draft-line mr-2"></i>
                Rascunhos ({draftPosts.length})
              </button>
            </div>

            <div className="divide-y divide-gray-200">
              {(activeTab === 'published' ? publishedPosts : draftPosts).length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                    <i
                      className={`${
                        activeTab === 'published' ? 'ri-article-line' : 'ri-draft-line'
                      } text-2xl text-gray-400`}
                    ></i>
                  </div>
                  <p className="text-gray-500">
                    {activeTab === 'published' ? 'Nenhum artigo publicado ainda.' : 'Nenhum rascunho guardado.'}
                  </p>
                  <button onClick={() => setShowForm(true)} className="mt-4 text-red-600 hover:text-red-700 font-medium cursor-pointer">
                    Criar novo artigo
                  </button>
                </div>
              ) : (
                (activeTab === 'published' ? publishedPosts : draftPosts).map((post) => (
                  <div key={post.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-6">
                      {post.image_url ? (
                        <img src={post.image_url} alt={post.title} className="w-32 h-24 object-cover rounded-lg" />
                      ) : (
                        <div className="w-32 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                          <i className="ri-image-line text-2xl text-gray-400"></i>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <h3 className="text-lg font-bold text-gray-900 mb-2 truncate">{post.title}</h3>
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{post.excerpt}</p>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <i className="ri-calendar-line"></i>
                                {post.published_at
                                  ? new Date(post.published_at).toLocaleDateString('pt-PT')
                                  : 'Não publicado'}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            {activeTab === 'drafts' ? (
                              <button
                                onClick={() => handlePublishDraft(post)}
                                className="w-10 h-10 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                                title="Publicar"
                              >
                                <i className="ri-send-plane-line"></i>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUnpublish(post)}
                                className="w-10 h-10 flex items-center justify-center bg-yellow-100 text-yellow-600 rounded-lg hover:bg-yellow-200 transition-colors cursor-pointer"
                                title="Mover para rascunhos"
                              >
                                <i className="ri-draft-line"></i>
                              </button>
                            )}
                            <button
                              onClick={() => handleEdit(post)}
                              className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                              title="Editar"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(post.id)}
                              className="w-10 h-10 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                              title="Excluir"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);   opacity: 1; }
        }
        .animate-slide-in { animation: slide-in 0.3s ease-out; }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

function LoginForm({ onLogin }: { onLogin: (email: string, password: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await onLogin(email, password);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-red-950 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
            <i className="ri-admin-line text-3xl text-red-600"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Painel Admin
          </h1>
          <p className="text-gray-600">Faça login para gerir o blog</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
              placeholder="seu@email.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Senha</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-7
          00 transition-colors font-medium whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {loading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-login-box-line"></i>}
            Entrar
          </button>
        </form>
      </div>
    </div>
  );
}
